import {
  BaseError,
  ContractFunctionExecutionError,
  ContractFunctionRevertedError,
  EstimateGasExecutionError,
  InsufficientFundsError,
  TransactionExecutionError,
  UserRejectedRequestError,
} from "viem";
import { MetaMaskMobileBridgeError } from "@/lib/wallet/errors";

// ─── Public classifier types ──────────────────────────────────────────────────

export type ContractErrorKey =
  | "userRejected"
  | "insufficientFunds"
  | "insufficientFee"
  | "contractReverted"
  | "gasEstimation"
  | "chainMismatch"
  | "walletNotConnected"
  | "transactionFailed"
  | "accountNotActive"
  | "insufficientToken"
  | "metamaskMobileBridge";

export type ContractErrorResult =
  | { kind: "key"; key: ContractErrorKey }
  | { kind: "raw"; message: string };

// ─── Contract-specific error handlers ────────────────────────────────────────

/**
 * Contract-specific error handlers
 * Add custom error handlers here for specific contract revert reasons
 */
type ContractErrorHandler = (
  error: ContractFunctionRevertedError,
) => string | null;

const contractErrorHandlers: ContractErrorHandler[] = [];

/**
 * Register a custom contract error handler
 * @param handler - Function that returns Persian message or null if not handled
 */
export function registerContractErrorHandler(handler: ContractErrorHandler) {
  contractErrorHandlers.push(handler);
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Check if error message contains specific text (case-insensitive)
 */
function errorContains(error: Error, text: string): boolean {
  return error.message.toLowerCase().includes(text.toLowerCase());
}

/**
 * Type guard: checks whether an Error also carries a shortMessage property.
 * Viem's BaseError has this; plain JS Error does not.
 */
function hasShortMessage(
  error: Error,
): error is Error & { shortMessage: string } {
  return (
    "shortMessage" in error &&
    typeof error.shortMessage === "string" &&
    error.shortMessage.length > 0
  );
}

// ─── Classifier (pure — no i18n, no toast) ───────────────────────────────────

/**
 * Classify a contract/wallet error into a stable key (for i18n) or a raw
 * passthrough string. Does NOT perform any translation or toast side-effects.
 */
export function classifyContractError(error: unknown): ContractErrorResult {
  // Handle null/undefined
  if (!error) {
    return { kind: "raw", message: String(error) };
  }

  // MetaMask Mobile bridge special-case (typed class — must come before generic Error branches)
  if (error instanceof MetaMaskMobileBridgeError) {
    return { kind: "key", key: "metamaskMobileBridge" };
  }

  // Handle non-Error objects
  if (!(error instanceof Error)) {
    if (typeof error === "string") {
      return { kind: "raw", message: error };
    }
    if (typeof error === "object" && error !== null) {
      const obj: Record<string, unknown> = { ...error };
      // NestJS class-validator returns `{ statusCode, message: string[], error }`
      if (
        Array.isArray(obj.message) &&
        obj.message.every((m) => typeof m === "string")
      ) {
        return { kind: "raw", message: (obj.message as string[]).join(" — ") };
      }
      if (typeof obj.message === "string" && obj.message.length > 0) {
        return { kind: "raw", message: obj.message };
      }
      if (typeof obj.error === "string" && obj.error.length > 0) {
        return { kind: "raw", message: obj.error };
      }
      try {
        return { kind: "raw", message: JSON.stringify(obj) };
      } catch {
        // circular / BigInt — fall through
      }
    }
    return { kind: "raw", message: String(error) };
  }

  // User rejected transaction
  if (error instanceof UserRejectedRequestError) {
    return { kind: "key", key: "userRejected" };
  }

  // Check for user rejection in nested errors or message
  if (
    errorContains(error, "user rejected") ||
    errorContains(error, "user denied") ||
    errorContains(error, "rejected the request")
  ) {
    return { kind: "key", key: "userRejected" };
  }

  // Insufficient funds
  if (error instanceof InsufficientFundsError) {
    return { kind: "key", key: "insufficientFunds" };
  }

  if (
    errorContains(error, "insufficient funds") ||
    errorContains(error, "insufficient balance")
  ) {
    return { kind: "key", key: "insufficientFunds" };
  }

  // Insufficient fee/gas
  if (
    errorContains(error, "insufficient fee") ||
    errorContains(error, "gas too low") ||
    errorContains(error, "intrinsic gas too low")
  ) {
    return { kind: "key", key: "insufficientFee" };
  }

  // Gas estimation errors
  if (error instanceof EstimateGasExecutionError) {
    return { kind: "key", key: "gasEstimation" };
  }

  // Contract function reverted
  if (error instanceof ContractFunctionRevertedError) {
    if (error.data?.errorName === "InsufficientFunds") {
      return { kind: "key", key: "insufficientToken" };
    }
    // Try custom handlers first
    for (const handler of contractErrorHandlers) {
      const message = handler(error);
      if (message) {
        return { kind: "raw", message };
      }
    }
    // Show raw revert reason from contract
    if (error.reason) {
      return { kind: "raw", message: error.reason };
    }
    if (error.shortMessage) {
      return { kind: "raw", message: error.shortMessage };
    }
    return { kind: "key", key: "contractReverted" };
  }

  // Contract function execution error (includes reverts)
  if (error instanceof ContractFunctionExecutionError) {
    // Check for nested revert error
    if (error.cause instanceof ContractFunctionRevertedError) {
      if (error.cause.data?.errorName === "InsufficientFunds") {
        return { kind: "key", key: "insufficientToken" };
      }
      for (const handler of contractErrorHandlers) {
        const message = handler(error.cause);
        if (message) {
          return { kind: "raw", message };
        }
      }
      if (error.cause.reason) {
        return { kind: "raw", message: error.cause.reason };
      }
      if (error.cause.shortMessage) {
        return { kind: "raw", message: error.cause.shortMessage };
      }
    }
    if (error.shortMessage) {
      return { kind: "raw", message: error.shortMessage };
    }
    return { kind: "key", key: "contractReverted" };
  }

  // Transaction execution error
  if (error instanceof TransactionExecutionError) {
    return { kind: "key", key: "transactionFailed" };
  }

  // Chain/network mismatch
  if (
    errorContains(error, "chain mismatch") ||
    errorContains(error, "wrong network") ||
    errorContains(error, "network mismatch")
  ) {
    return { kind: "key", key: "chainMismatch" };
  }

  // Wallet not connected
  if (
    errorContains(error, "not connected") ||
    errorContains(error, "no account") ||
    errorContains(error, "wallet not connected")
  ) {
    return { kind: "key", key: "walletNotConnected" };
  }

  // Account not active
  if (
    errorContains(error, "account not active") ||
    errorContains(error, "account is not active") ||
    errorContains(error, "inactive account")
  ) {
    return { kind: "key", key: "accountNotActive" };
  }

  // Progressive decode: shortMessage (viem BaseError or any Error with shortMessage) → message → toString()
  if (error instanceof BaseError && error.shortMessage) {
    return { kind: "raw", message: error.shortMessage };
  }
  if (hasShortMessage(error)) {
    return { kind: "raw", message: error.shortMessage };
  }
  return { kind: "raw", message: error.message || error.toString() };
}

/**
 * Classify an API error. All branches return raw messages (no key cases today).
 */
export function classifyApiError(error: unknown): ContractErrorResult {
  if (!error) {
    return { kind: "raw", message: String(error) };
  }

  if (typeof error === "string") {
    return { kind: "raw", message: error };
  }

  if (error instanceof Error) {
    // Try to extract from error.data (openapi-react-query structure).
    const data = Reflect.get(error, "data");

    if (data !== null && data !== undefined && typeof data === "object") {
      const errors = Reflect.get(data, "errors");

      // Handle validation errors (array of strings)
      if (Array.isArray(errors) && errors.every((e) => typeof e === "string")) {
        return { kind: "raw", message: errors.join(", ") };
      }

      // Handle field validation errors (object with field name → string[] entries)
      if (
        errors !== null &&
        errors !== undefined &&
        typeof errors === "object" &&
        !Array.isArray(errors)
      ) {
        const fieldErrors = Object.values(errors)
          .flatMap((v) =>
            Array.isArray(v) ? v.filter((s) => typeof s === "string") : [],
          )
          .join(", ");
        return { kind: "raw", message: fieldErrors };
      }

      // Try message, error, or detail fields
      const dataMessage = Reflect.get(data, "message");
      if (typeof dataMessage === "string" && dataMessage.length > 0) {
        return { kind: "raw", message: dataMessage };
      }
      const dataError = Reflect.get(data, "error");
      if (typeof dataError === "string" && dataError.length > 0) {
        return { kind: "raw", message: dataError };
      }
      const dataDetail = Reflect.get(data, "detail");
      if (typeof dataDetail === "string" && dataDetail.length > 0) {
        return { kind: "raw", message: dataDetail };
      }
    }

    // Check error.response.data structure
    const response = Reflect.get(error, "response");
    if (
      response !== null &&
      response !== undefined &&
      typeof response === "object"
    ) {
      const responseData = Reflect.get(response, "data");
      if (
        responseData !== null &&
        responseData !== undefined &&
        typeof responseData === "object"
      ) {
        const respMessage = Reflect.get(responseData, "message");
        if (typeof respMessage === "string" && respMessage.length > 0) {
          return { kind: "raw", message: respMessage };
        }
        const respError = Reflect.get(responseData, "error");
        if (typeof respError === "string" && respError.length > 0) {
          return { kind: "raw", message: respError };
        }
        const respDetail = Reflect.get(responseData, "detail");
        if (typeof respDetail === "string" && respDetail.length > 0) {
          return { kind: "raw", message: respDetail };
        }
      }
    }

    // Progressive decode: shortMessage → message → toString()
    if (hasShortMessage(error)) {
      return { kind: "raw", message: error.shortMessage };
    }
    return { kind: "raw", message: error.message || error.toString() };
  }

  // Handle objects with message property
  if (typeof error === "object" && error !== null) {
    const message = Reflect.get(error, "message");
    if (typeof message === "string") {
      return { kind: "raw", message };
    }
    if (Array.isArray(message) && message.every((m) => typeof m === "string")) {
      return { kind: "raw", message: message.join(" — ") };
    }
    const errField = Reflect.get(error, "error");
    if (typeof errField === "string") {
      return { kind: "raw", message: errField };
    }
  }

  return { kind: "raw", message: String(error) };
}

// ─── isUserActionError ────────────────────────────────────────────────────────

/**
 * Check if error is a user action (rejection, etc.) that shouldn't be reported
 */
export function isUserActionError(error: unknown): boolean {
  if (!error) return false;
  if (!(error instanceof Error)) return false;

  // User rejected transaction
  if (error instanceof UserRejectedRequestError) {
    return true;
  }

  // Check for user rejection in message
  const errorMessage = error.message.toLowerCase();
  return (
    errorMessage.includes("user rejected") ||
    errorMessage.includes("user denied") ||
    errorMessage.includes("rejected the request")
  );
}
